<script setup>
import TourStepCounter from "@/components/tours/TourStepCounter.vue";
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { Popper, PopperContent } from "floating-vue";
import {disableScroll, enableScroll} from "@/composables/useScroll";

defineOptions({
  name: "TourTooltip",
  inheritAttrs: false,
});

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  step: {
    type: String,
    required: true,
  },
  tourId: {
    type: String,
    required: true,
  },
  target: {
    type: String,
    required: true,
  },
  placement: {
    type: String,
    default: "bottom",
  },
  nextStepText: {
    type: String,
    default: "Next",
  },
});

const store = useStore();
const VPopper = Popper();
const theme = "primary-tooltip";

const targetElement = ref(null);
const popperContentRef = ref(null);
const isShown = ref(false);
const retryHandle = ref(null);
const targetWarningLogged = ref(false);
const outsideClickEvents = ["click", "mousedown", "mouseup", "touchstart", "touchend"];

const shouldShow = computed(() =>
  store.getters["tours/showTourStep"]({ tourId: props.tourId, step: props.step }),
);
const canGoToPrevStep = computed(() => store.getters["tours/canGoToPrevStep"]);

const getTargetNodes = () => (targetElement.value ? [targetElement.value] : []);
const getReferenceNode = () => targetElement.value || null;
const getPopperNode = () => popperContentRef.value?.$el || null;

const clearRetry = () => {
  if (retryHandle.value !== null) {
    if (typeof window !== "undefined") {
      window.clearTimeout(retryHandle.value);
    }
    retryHandle.value = null;
  }
};

const resolveTarget = () => {
  if (typeof window === "undefined") {
    return;
  }

  const element = document.querySelector(props.target);

  if (!element) {
    if (!targetWarningLogged.value) {
      console.warn(`Target element ${props.target} not found`);
      targetWarningLogged.value = true;
    }
    targetElement.value = null;
    return;
  }

  targetElement.value = element;
  targetWarningLogged.value = false;
};

const scrollToTarget = () => {
  if (typeof window === "undefined") {
    return;
  }

  const element = targetElement.value;

  if (!element || typeof element.scrollIntoView !== "function") {
    return;
  }

  element.scrollIntoView({
    behavior: "smooth",
    block: "center",
    inline: "center",
  });
};

const attemptShow = async (retryCount = 0) => {
  if (typeof window === "undefined") {
    return;
  }

  clearRetry();

  window.setTimeout(() => {
    resolveTarget();

    if (!targetElement.value) {
      if (retryCount < 5) {
        retryHandle.value = window.setTimeout(() => attemptShow(retryCount + 1), 150);
      }
      return;
    }

    scrollToTarget();

    isShown.value = true;
  }, 200);
};

const hidePopper = () => {
  clearRetry();
  isShown.value = false;
};

const goToNextStep = () => store.dispatch("tours/goToNextStep");
const goToPrevStep = () => store.commit("tours/goToPrevStep");

const handleOutsideClick = (event) => {
  const popperNode = getPopperNode();

  if (popperNode && popperNode.contains(event.target)) {
    return;
  }

  event.stopPropagation();
  event.preventDefault();
};

const addOutsideClickBlockers = () => {
  if (typeof window === "undefined") {
    return;
  }

  outsideClickEvents.forEach((eventName) => {
    document.addEventListener(eventName, handleOutsideClick, true);
  });
};

const removeOutsideClickBlockers = () => {
  if (typeof window === "undefined") {
    return;
  }

  outsideClickEvents.forEach((eventName) => {
    document.removeEventListener(eventName, handleOutsideClick, true);
  });
};

watch(
  shouldShow,
  (newShouldShow) => {
    if (!newShouldShow) {
      targetElement.value = null;
      hidePopper();
      enableScroll();
      return;
    }

    resolveTarget();
    attemptShow();
    disableScroll();
  },
  { immediate: true },
);

watch(
  () => props.target,
  () => {
    resolveTarget();
    if (shouldShow.value) {
      attemptShow();
    }
  },
);

watch(
  isShown,
  (newIsShown) => {
    if (newIsShown) {
      addOutsideClickBlockers();
    } else {
      removeOutsideClickBlockers();
    }
  },
);

onMounted(() => {
  resolveTarget();
});

onBeforeUnmount(() => {
  hidePopper();
  enableScroll();
  removeOutsideClickBlockers();
});
</script>

<template>
  <component
    :is="VPopper"
    v-if="targetElement"
    v-slot="slotProps"
    :theme="theme"
    :shown="isShown"
    :triggers="[]"
    :placement="placement"
    :target-nodes="getTargetNodes"
    :reference-node="getReferenceNode"
    :popper-node="getPopperNode"
  >
    <PopperContent
      ref="popperContentRef"
      :popper-id="slotProps.popperId"
      :theme="theme"
      :shown="slotProps.isShown"
      :mounted="slotProps.shouldMountContent"
      :skip-transition="slotProps.skipTransition"
      :auto-hide="slotProps.autoHide"
      :handle-resize="slotProps.handleResize"
      :classes="slotProps.classes"
      :result="slotProps.result"
      @hide="hidePopper"
      @resize="slotProps.onResize"
    >
      <div class="tailwind">
        <div class="tw-flex tw-flex-col tw-gap-8 tw-p-4 tw-text-white tw-max-w-[30rem]">
          <div class="tw-flex tw-justify-between tw-items-center tw-gap-x-4">
            <h4 class="tw-font-bold tw-text-white tw-text-base">
              {{ title }}
            </h4>
          </div>

          <div class="tw-text-white tw-text-base">
            <slot name="default" />
          </div>

          <div class="tw-flex tw-justify-between tw-items-center tw-gap-x-4">
            <TourStepCounter class="tw-mr-16" />

            <div class="tw-flex tw-items-center tw-gap-x-3">
              <DefaultButton
                v-if="canGoToPrevStep"
                variant="primary-outlined"
                size="tooltip"
                @click="goToPrevStep"
              >
                Back
              </DefaultButton>

              <DefaultButton
                variant="primary-inverted"
                size="tooltip"
                @click="goToNextStep"
              >
                {{ nextStepText }}
              </DefaultButton>
            </div>
          </div>
        </div>
      </div>
    </PopperContent>
  </component>
</template>